import { Injectable } from '@angular/core';
import API, { graphqlOperation, GraphQLResult } from '@aws-amplify/api-graphql';
import { ClientStatus, ContactFormMutation, ModelContactFormInput, TF } from 'src/app/API.service';

export type GetClientQuery = {
  __typename: 'Client';
  id: string;
  username: string;
  kontoMappings?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  address: string;
  city: string;
  logoUrl: string;
  createdAt: string;
  updatedAt: string;
  expirationDate?: string | null;
  isVatIncluded?: TF | null;
  status?: ClientStatus | null;
  userID?: string | null;
  totalScanCount?: number | null;
  cicDriveFileId?: string | null;
  tin?: string | null;
  municipality?: string | null;
  activityCode?: string | null;
  activityName?: string | null;
  bankAccount?: string | null;
  bankName?: string | null;
  payrollSettings?: string | null;
  folderScanTasks?: {
    __typename: "ModelFolderScanTaskConnection";
    nextToken?: string | null;
  } | null;
  folders?: {
    __typename: 'ModelFolderConnection';
    items: Array<{
      __typename: 'Folder';
      id: string;
      name: string;
      clientID?: string | null;
      downloadLink?: string | null;
      createdAt: string;
      updatedAt: string;
      invoices?: {
        __typename: 'ModelInvoiceConnection';
        items: Array<{
          __typename: 'Invoice';
          id: string;
          name: string;
          folderID: string;
          createdAt: string;
          updatedAt: string;
        } | null>;
        nextToken?: string | null;
      } | null;
    } | null>;
    nextToken?: string | null;
  } | null;
};

export type UpdateClientInput = {
  id: string;
  username?: string | null;
  email?: string | null;
  company?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  tin?: string | null;
  municipality?: string | null;
  activityCode?: string | null;
  activityName?: string | null;
  bankAccount?: string | null;
  bankName?: string | null;
  payrollSettings?: string | null;
  kontoMappings?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type ModelStringInput = {
  ne?: string | null;
  eq?: string | null;
  le?: string | null;
  lt?: string | null;
  ge?: string | null;
  gt?: string | null;
  contains?: string | null;
  notContains?: string | null;
  between?: Array<string | null> | null;
  beginsWith?: string | null;
  attributeExists?: boolean | null;
  attributeType?: ModelAttributeTypes | null;
  size?: ModelSizeInput | null;
};

export type ModelSizeInput = {
  ne?: number | null;
  eq?: number | null;
  le?: number | null;
  lt?: number | null;
  ge?: number | null;
  gt?: number | null;
  between?: Array<number | null> | null;
};

export type UpdateClientMutation = {
  __typename: 'Client';
  id: string;
  username: string;
  email: string;
  company: string;
  phone: string;
  address: string;
  city: string;
  createdAt: string;
  updatedAt: string;
  folders?: {
    __typename: 'ModelFolderConnection';
    items: Array<{
      __typename: 'Folder';
      id: string;
      name: string;
      clientID?: string | null;
      downloadLink?: string | null;
      createdAt: string;
      updatedAt: string;
      invoices?: {
        __typename: 'ModelInvoiceConnection';
        items: Array<{
          __typename: 'Invoice';
          id: string;
          name: string;
          folderID: string;
          createdAt: string;
          updatedAt: string;
        } | null>;
        nextToken?: string | null;
      } | null;
    } | null>;
    nextToken?: string | null;
  } | null;
};

export enum ModelAttributeTypes {
  binary = 'binary',
  binarySet = 'binarySet',
  bool = 'bool',
  list = 'list',
  map = 'map',
  number = 'number',
  numberSet = 'numberSet',
  string = 'string',
  stringSet = 'stringSet',
  _null = '_null',
}

export type ModelClientConditionInput = {
  username?: ModelStringInput | null;
  email?: ModelStringInput | null;
  company?: ModelStringInput | null;
  phone?: ModelStringInput | null;
  address?: ModelStringInput | null;
  city?: ModelStringInput | null;
  and?: Array<ModelClientConditionInput | null> | null;
  or?: Array<ModelClientConditionInput | null> | null;
  not?: ModelClientConditionInput | null;
};

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  async GetClient(id: string): Promise<GetClientQuery> {
    const statement = `query GetClient($id: ID!) {
        getClient(id: $id) {
          __typename
          id
          firstName
          lastName
          username
          email
          company
          phone
          address
          city
          logoUrl
          createdAt
          updatedAt
          status
          totalScanCount
          expirationDate
          isVatIncluded
          userID
          viberID
          cicDriveFileId
          tin
          municipality
          activityCode
          activityName
          bankAccount
          bankName
          payrollSettings
          folderScanTasks {
            __typename
            nextToken
          }
          folders {
            __typename
            items {
              __typename
              id
              name
              clientID
              downloadLink
              createdAt
              updatedAt
              invoices {
                __typename
                items {
                  __typename
                  id
                  name
                  folderID
                  createdAt
                  updatedAt
                }
                nextToken
              }
            }
            nextToken
          }
        }
      }`;
    const gqlAPIServiceArguments: any = {
      id,
    };
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <GetClientQuery>response.data.getClient;
  }


  async ContactForm(
    input?: ModelContactFormInput
  ): Promise<ContactFormMutation> {
    const statement = `mutation ContactForm($input: ModelContactFormInput) {
        contactForm(input: $input) {
          __typename
          result
          message
        }
      }`;
    const gqlAPIServiceArguments: any = {};
    if (input) {
      gqlAPIServiceArguments.input = input;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <ContactFormMutation>response.data.contactForm;
  }

  async GetClientStatus(id: string): Promise<GetClientQuery> {
    const statement = `query GetClient($id: ID!) {
        getClient(id: $id) {
          __typename
          id
          username
          expirationDate
          status
        }
      }`;
    const gqlAPIServiceArguments: any = {
      id,
    };
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <GetClientQuery>response.data.getClient;
  }

  async UpdateClient(
    input: UpdateClientInput,
    condition?: ModelClientConditionInput
  ): Promise<UpdateClientMutation> {
    const statement = `mutation UpdateClient($input: UpdateClientInput!, $condition: ModelClientConditionInput) {
        updateClient(input: $input, condition: $condition) {
          __typename
          id
          username
          email
          company
          phone
          address
          city
          createdAt
          updatedAt
          isVatIncluded
          tin
          municipality
          activityCode
          activityName
          bankAccount
          bankName
          payrollSettings
          folders {
            __typename
            items {
              __typename
              id
              name
              clientID
              downloadLink
              createdAt
              updatedAt
              invoices {
                __typename
                items {
                  __typename
                  id
                  name
                  folderID
                  createdAt
                  updatedAt
                }
                nextToken
              }
            }
            nextToken
          }
        }
      }`;
    const gqlAPIServiceArguments: any = {
      input,
    };
    if (condition) {
      gqlAPIServiceArguments.condition = condition;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <UpdateClientMutation>response.data.updateClient;
  }
}
